<script lang="ts">
    /**
     * Small sortable table with optional inline bars. Numeric columns sort
     * numerically (a lexical sort would put 100 before 20), nulls always sort
     * last whichever direction is active, and a null renders as a dash so an
     * empty cell never reads as a zero.
     */
    export let columns: { key: string; label: string; numeric?: boolean; bar?: boolean }[] = [];
    export let rows: Record<string, string | number | null>[] = [];
    export let defaultSort = '';

    function column(key: string) {
        return columns.find(c => c.key === key);
    }

    function initialDir(key: string): 'asc' | 'desc' {
        return column(key)?.numeric ? 'desc' : 'asc';
    }

    let sortKey = defaultSort;
    let sortDir: 'asc' | 'desc' = initialDir(defaultSort);

    function onHeader(key: string) {
        if (sortKey === key) {
            sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            sortKey = key;
            sortDir = initialDir(key);
        }
    }

    function fmt(v: string | number | null): string {
        if (v === null || v === undefined) return '-';
        return typeof v === 'number' ? v.toLocaleString() : String(v);
    }

    function pct(v: string | number | null, max: number): number {
        const n = Number(v ?? 0);
        if (!max || !Number.isFinite(n)) return 0;
        return Math.max(0, Math.min(100, (n / max) * 100));
    }

    $: sortedNumeric = !!column(sortKey)?.numeric;
    $: sorted = [...rows].sort((a, b) => {
        const av = a[sortKey] ?? null;
        const bv = b[sortKey] ?? null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        const c = sortedNumeric
            ? Number(av) - Number(bv)
            : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? c : -c;
    });
    $: maxes = Object.fromEntries(
        columns
            .filter(c => c.bar)
            .map(c => [c.key, Math.max(0, ...rows.map(r => Number(r[c.key] ?? 0)))]),
    );
</script>

<div class="table-wrap">
    <table>
        <thead>
            <tr>
                {#each columns as c}
                    <th
                        scope="col"
                        class:numeric={c.numeric}
                        aria-sort={sortKey === c.key
                            ? sortDir === 'asc'
                                ? 'ascending'
                                : 'descending'
                            : 'none'}>
                        <button type="button" on:click={() => onHeader(c.key)}>
                            {c.label}{#if sortKey === c.key}&nbsp;<span class="sort-caret" aria-hidden="true"
                                    >{sortDir === 'asc' ? '↑' : '↓'}</span
                                >{/if}
                        </button>
                    </th>
                {/each}
            </tr>
        </thead>
        <tbody>
            {#each sorted as r}
                <tr>
                    {#each columns as c}
                        <td class:numeric={c.numeric}>
                            {#if c.bar}
                                <span
                                    class="bar"
                                    style="width: {pct(r[c.key], maxes[c.key])}%; top: 6px; bottom: 6px;"
                                ></span>
                            {/if}
                            <span class="val">{fmt(r[c.key])}</span>
                        </td>
                    {/each}
                </tr>
            {/each}
        </tbody>
    </table>
</div>

<style>
    .table-wrap {
        overflow-x: auto;
    }
    table {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--font-body);
        font-size: 0.82rem;
    }
    th,
    td {
        text-align: left;
        padding: var(--s-2) var(--s-3);
        border-bottom: 1px solid var(--rule);
        position: relative;
    }
    th.numeric,
    td.numeric {
        text-align: right;
    }
    td.numeric .val {
        font-family: var(--font-mono);
        font-variant-numeric: tabular-nums;
    }
    th button {
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        color: var(--ink-muted);
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
    }
    th button:hover {
        color: var(--ink);
    }
    .sort-caret {
        margin-left: 2px;
        text-transform: none;
        white-space: nowrap;
    }
    .bar {
        position: absolute;
        left: 0;
        border-radius: 2px;
        background: var(--leaf-soft);
        z-index: 0;
    }
    .val {
        position: relative;
        z-index: 1;
    }
</style>
